// src/components/SupplyPanel.tsx — SUPPLY (Oct 29, 2026, record 100; 00044 v2.2 materials rule).
// Where Green Maestro delivers local materials: one category in one market, switched on by hand when a real supplier deal
// exists. Off (the default) = the contractor buys from his materials list. Every switch needs a reason and is logged with the
// other Ops changes (pricing_policy_log, policy "supply_zip"). Kits and Christmas lights ship everywhere and are not switched here.
import { useCallback, useEffect, useState } from "react";
import { canApply, ops, SUPPLY_CATEGORIES, type SupplyCategory, type SupplyList, type SupplyOrder } from "../lib/ops";
import { useOperator, useToast } from "../lib/context";
import { Field, Loading, Notice } from "./ui";

const label = (c: string) => SUPPLY_CATEGORIES.find((x) => x.key === c)?.label ?? c;
const short = (c: string) => label(c).split(" (")[0];
const usd = (c: number | null | undefined) => (c == null ? "" : `$${(c / 100).toFixed(2)}`);

export function SupplyPanel({ zip }: { zip: string | null }) {
  const op = useOperator();
  const toast = useToast();
  const [data, setData] = useState<SupplyList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cat, setCat] = useState<SupplyCategory>("hardscape");
  const [sup, setSup] = useState("");
  const [zips, setZips] = useState(zip ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await ops.listSupply(zip ?? undefined));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [zip]);
  useEffect(() => {
    void load();
    setZips(zip ?? "");
  }, [load, zip]);

  const zipList = zips.split(/[\s,]+/).map((z) => z.trim()).filter(Boolean);
  const zipsOk = zipList.length > 0 && zipList.every((z) => /^\d{5}$/.test(z));
  const suppliers = (data?.suppliers ?? []).filter((s) => s.type === cat);
  const chosen = suppliers.find((s) => s.id === sup);
  const may = canApply(op.role);
  const why = reason.trim();

  const apply = async (active: boolean, zipsArg: string[], category: SupplyCategory, supplierId: string | null) => {
    setBusy(true);
    try {
      const r = await ops.setSupply(zipsArg, category, supplierId, active, why);
      toast(`✓ ${short(category)} ${active ? "on" : "off"} in ${r.zips} ZIP${r.zips === 1 ? "" : "s"}`);
      setReason("");
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="editor-section card">
      <h2 className="card-title">Local supply{zip ? ` · ${zip}` : ""}</h2>
      <p className="muted">
        Where Green Maestro delivers local materials to contractors' jobs. Off means the contractor buys them from his materials
        list. Turn a category on only when a real supplier deal exists. Kits and Christmas lights ship everywhere and aren't
        switched here.
      </p>
      {error && <Notice kind="error">{error}</Notice>}
      {!data && !error && <Loading what="local supply" />}
      {data && data.rows.length === 0 && (
        <p className="empty">{zip ? `No local supply in ${zip}: contractors buy everything local.` : "No local supply anywhere yet."}</p>
      )}
      {data && data.rows.length > 0 && (
        <div className="table-scroll">
          <table className="zip-table">
            <thead>
              <tr>
                <th scope="col">ZIP</th>
                <th scope="col">Category</th>
                <th scope="col">Supplier</th>
                <th scope="col">State</th>
                <th scope="col">Why · who</th>
                <th scope="col">Change</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={`${r.zip}|${r.category}`}>
                  <td className="mono">{r.zip}</td>
                  <td>{short(r.category)}</td>
                  <td>{r.supplier_name ?? "—"}</td>
                  <td>{r.active ? "On" : "Off"}</td>
                  <td className="muted">
                    {r.reason ?? ""}
                    {r.by ? ` · ${r.by}` : ""}
                    {r.at ? ` · ${new Date(r.at).toLocaleDateString("en-US")}` : ""}
                  </td>
                  <td>
                    {may && r.active && (
                      <button
                        type="button"
                        className="btn"
                        disabled={busy || why.length < 5}
                        title={why.length < 5 ? "Write a reason below first" : undefined}
                        onClick={() => void apply(false, [r.zip], r.category, null)}
                      >
                        Turn off
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {may ? (
        <>
          <div className="fields">
            <Field label="Category" htmlFor="sup-cat">
              <select
                id="sup-cat"
                value={cat}
                onChange={(e) => {
                  setCat(e.target.value as SupplyCategory);
                  setSup("");
                }}
              >
                {SUPPLY_CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Supplier"
              htmlFor="sup-sup"
              hint={chosen && !chosen.orderable ? "No live ordering channel yet — Ops orders by hand" : undefined}
            >
              <select id="sup-sup" value={sup} onChange={(e) => setSup(e.target.value)}>
                <option value="">{suppliers.length ? "Choose a supplier" : "No supplier of this type yet"}</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.orderable ? "" : " (no live channel)"}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="ZIPs" htmlFor="sup-zips" hint="One or more, separated by commas">
              <input id="sup-zips" inputMode="numeric" value={zips} onChange={(e) => setZips(e.target.value)} placeholder="98223, 98258" />
            </Field>
            <Field label="Reason" htmlFor="sup-why" hint="Required, 5+ characters — it goes to the change log">
              <input id="sup-why" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Mutual Materials delivers here" />
            </Field>
          </div>
          <button
            type="button"
            className="btn primary"
            disabled={busy || !zipsOk || !sup || why.length < 5}
            onClick={() => void apply(true, zipList, cat, sup)}
          >
            Turn on {short(cat)}
            {zipsOk ? ` in ${zipList.length} ZIP${zipList.length === 1 ? "" : "s"}` : ""}
          </button>
        </>
      ) : (
        <p className="muted">Only pricing admins and the owner can switch local supply.</p>
      )}
    </section>
  );
}

/** Orders page: paid contractor jobs whose local materials Green Maestro delivers — build each order with the job panel. */
export function SupplyQueue({ onFind }: { onFind: (jobId: string) => void }) {
  const [rows, setRows] = useState<SupplyOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    ops.supplyOrders().then(setRows).catch((e) => setError((e as Error).message));
  }, []);
  if (error) return <Notice kind="error">{error}</Notice>;
  if (!rows || rows.length === 0) return null;
  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <h2 className="card-title">Contractor jobs: materials to order</h2>
      <p className="muted">
        Paid quotes where Green Maestro delivers local materials. Find the job, add its lines and send the order as usual; delivery is
        quoted with the order.
      </p>
      <div className="table-scroll">
        <table className="zip-table">
          <thead>
            <tr>
              <th scope="col">Quote</th>
              <th scope="col">Contractor</th>
              <th scope="col">ZIP</th>
              <th scope="col">Category · supplier</th>
              <th scope="col">Materials</th>
              <th scope="col">Est.</th>
              <th scope="col">PO</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.quote_id}|${r.category}`}>
                <td className="mono">{r.quote_number}</td>
                <td>{r.contractor ?? "—"}</td>
                <td className="mono">{r.zip}</td>
                <td>
                  {short(r.category)} · {r.supplier_name ?? "—"}
                </td>
                <td>
                  {r.rows.map((m, i) => (
                    <div key={i}>
                      {m.description}
                      {m.ref ? <span className="muted"> · {m.ref}</span> : null}
                    </div>
                  ))}
                </td>
                <td className="mono">{usd(r.est_cents)}</td>
                <td className="mono">{r.po ?? "—"}</td>
                <td>
                  <button type="button" className="btn" onClick={() => onFind(r.obra_job_id)}>
                    Find job
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
